import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { PackagePlus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { NativeSelect } from "@/components/common";
import { CustStatusBadge } from "@/components/badges";
import { SupplyDialog } from "@/components/SupplyDialog";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg } from "@/lib/api";
import { num, rupiah, supplyLabelOf } from "@/lib/format";

export function SupplyPanel({ period }) {
  const { can } = useAuth();
  const [rows, setRows] = useState([]);
  const [cands, setCands] = useState([]);
  const [pick, setPick] = useState("");
  const [target, setTarget] = useState(null);
  const load = useCallback(async () => {
    try {
      const [s, c] = await Promise.all([api.get("/supplies", { params: { period } }), api.get("/customers")]);
      setRows(s.data);
      setCands(c.data.filter((x) => x.status === "baru" || x.status === "aktif_kembali"));
    } catch (e) { toast.error(errMsg(e)); }
  }, [period]);
  useEffect(() => {
    load();
  }, [load]);
  const byId = Object.fromEntries(cands.map((c) => [c.id, c]));
  return (
    <div className="mb-4 border border-violet-200 bg-violet-50/40 rounded-sm p-3 space-y-2" data-testid="supply-panel">
      <div className="flex flex-wrap items-center gap-2">
        <div className="font-display font-semibold text-sm flex-1 min-w-[200px]">Pasokan Awal (pelanggan baru / aktif kembali) · <span className="text-violet-800">{supplyLabelOf(period)}</span></div>
        {can("estimate:write") && (
          <>
            <NativeSelect value={pick} onChange={(e) => setPick(e.target.value)} data-testid="supply-customer-select">
              <option value="">Pilih pelanggan…</option>
              {cands.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </NativeSelect>
            <Button size="sm" disabled={!pick} onClick={() => setTarget(byId[pick])} className="bg-violet-700 hover:bg-violet-800" data-testid="supply-input-btn"><PackagePlus className="h-4 w-4 mr-2" />Input Pasokan Awal</Button>
          </>
        )}
      </div>
      <p className="text-xs text-slate-600">Pasokan awal diisi manual, tidak ditambah 30%, dan tidak dihitung sebagai penjualan aktual atau kewajiban top up. Hitung ulang estimasi setelah mengisi pasokan awal.</p>
      {rows.length > 0 && (
        <div className="xl-wrap">
          <table className="xl" data-testid="supply-table">
            <thead><tr><th>Pelanggan</th><th>Status</th><th>Ref</th><th>Rincian</th><th className="text-right">Qty</th><th className="text-right">Nilai (terpisah)</th></tr></thead>
            <tbody>
              {rows.map((s, i) => (
                <tr key={s.id} className={byId[s.customer_id] ? "cursor-pointer" : ""} onClick={() => byId[s.customer_id] && can("estimate:write") && setTarget(byId[s.customer_id])} data-testid={`supply-row-${i}`}>
                  <td className="font-medium">{s.customer_name}</td><td><CustStatusBadge status={s.kind} /></td><td className="font-mono text-xs">{s.ref}</td>
                  <td className="text-xs">{s.lines.map((l) => `${l.voucher_name}: ${l.qty}`).join(", ")}</td>
                  <td className="num">{num(s.total_qty)}</td><td className="num">{rupiah(s.total_value)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <SupplyDialog customer={target} period={period} onClose={() => setTarget(null)} onDone={load} />
    </div>
  );
}
